/**
 * Axios instance for the ZaVseh REST API.
 *
 * - baseURL from VITE_API_BASE_URL
 * - Request interceptor attaches `Authorization: Bearer <accessToken>` from localStorage
 * - Response interceptor handles 401: attempts a single in-flight token refresh
 *   (POST /auth/refresh with the stored refresh token), retries the original
 *   request on success, and on failure clears tokens and redirects to /login.
 *
 * Token storage is handled by `./tokens` (no dependency on the auth store) to
 * avoid circular imports. TASK-13 will integrate the Pinia auth store on top.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from './tokens'

const baseURL = import.meta.env.VITE_API_BASE_URL

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: false,
  timeout: 30_000,
})

// Augment config with internal retry flags.
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _isRefreshRequest?: boolean
}

// ─── Request interceptor: attach access token ───────────────────────────────

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ─── Refresh machinery (single-flight + queue) ──────────────────────────────

let isRefreshing = false
let pendingQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null): void {
  for (const pending of pendingQueue) {
    if (error) {
      pending.reject(error)
    } else {
      pending.resolve(token as string)
    }
  }
  pendingQueue = []
}

function redirectToLogin(): void {
  if (typeof window !== 'undefined') {
    // Hard redirect keeps things simple in the skeleton. TASK-13 may switch
    // this to a router-based navigation to preserve SPA state.
    const current = window.location.pathname + window.location.search
    if (!current.startsWith('/login')) {
      window.location.href = `/login?redirect=${encodeURIComponent(current)}`
    }
  }
}

async function performRefresh(): Promise<string> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }
  // Bare axios call (no interceptors) to avoid recursion on the refresh itself.
  const response = await axios.post<{ accessToken: string; refreshToken: string }>(
    `${baseURL}/auth/refresh`,
    { refreshToken },
  )
  const { accessToken, refreshToken: newRefreshToken } = response.data
  setTokens(accessToken, newRefreshToken)
  return accessToken
}

// ─── Response interceptor: 401 -> refresh + retry ───────────────────────────

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined

    // Only intercept 401 on "normal" requests (not the refresh call, not already retried).
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original._isRefreshRequest
    ) {
      // If a refresh is already in flight, queue this request until it resolves.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (token: string) => {
              original.headers.Authorization = `Bearer ${token}`
              resolve(api(original as AxiosRequestConfig))
            },
            reject,
          })
        })
      }

      original._retry = true
      isRefreshing = true
      try {
        const newToken = await performRefresh()
        processQueue(null, newToken)
        original.headers.Authorization = `Bearer ${newToken}`
        return api(original as AxiosRequestConfig)
      } catch (refreshError) {
        processQueue(refreshError, null)
        clearTokens()
        redirectToLogin()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

export default api
